import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PhoneCall } from "lucide-react";
import { api } from "../lib/api";
import { PageHeader, Empty } from "../components/common";
import { ReassignControl, CallbackScore, CallbackTrend } from "../components/CallbackTeam";
import { CallButton } from "../components/CallButton";

const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" });
const BR = { DEL: "Delhi (FBD)", MUM: "Mumbai" };

const Row = ({ l, team, onDone }) => (
  <div className="flex items-stretch gap-2">
  <Link to={`/sales/leads/${l.lead_id}`} data-testid={`callback-row-${l.lead_id}`} className="flex flex-1 min-w-0 flex-wrap items-center gap-x-4 gap-y-1 py-3 px-1 hover:text-primary">
    <span className={`text-xs px-2 py-0.5 border ${l.overdue ? "border-red-500 text-red-400" : l.is_due ? "border-primary text-primary" : "border-border text-muted-foreground"}`} data-testid={`callback-state-${l.lead_id}`}>{l.overdue ? "Overdue" : l.is_due ? "Due now" : "Upcoming"}</span>
    <span className="font-medium flex-1 min-w-0 truncate">{l.company_name || l.contact_name}</span>
    <span className="num text-sm hidden sm:inline">{fmt(l.due_at)}</span>
    <span className="w-full text-xs text-muted-foreground"><span className="sm:hidden num text-foreground">{fmt(l.due_at)} · </span>{team && <b className="text-foreground" data-testid={`callback-agent-${l.lead_id}`}>{l.agent} · </b>}{l.contact_name} · {l.lead_id}{l.branch ? ` · ${BR[l.branch] || l.branch}` : ""} · try {l.attempt} of 3</span>
  </Link>
  <CallButton l={l} testid={`callback-call-${l.lead_id}`} onDone={onDone} className="md:hidden" />
  </div>
);

const AgentSummary = ({ items, agent, setAgent }) => {
  const counts = items.reduce((m, i) => ({ ...m, [i.agent]: (m[i.agent] || 0) + 1 }), {});
  return (
    <div className="flex flex-wrap gap-2 mb-3" data-testid="callback-agent-filter">
      {["All", ...Object.keys(counts).sort()].map((a) => (
        <button key={a} data-testid={`callback-agent-chip-${a}`} onClick={() => setAgent(a)} className={`text-xs border px-2 py-1 ${agent === a ? "border-primary text-primary" : "text-muted-foreground"}`}>
          {a} <span className="num">{a === "All" ? items.length : counts[a]}</span></button>))}
    </div>
  );
};

const useCallbackData = (team, tick) => {
  const [d, setD] = useState(null);
  const [agents, setAgents] = useState([]);
  useEffect(() => { if (team) api.get("/sales/callbacks/agents").then((r) => setAgents(r.data)).catch(() => {}); }, [team]);
  useEffect(() => {
    const load = () => api.get("/sales/callbacks", { params: team ? { team: true } : {} }).then((r) => setD(r.data)).catch(() => setD({ items: [], due: 0, overdue: 0 }));
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [team, tick]);
  return { d, setD, agents };
};

const TeamTabs = ({ team, onPick }) => (
  <div className="flex gap-2 mb-4">
    {[[false, "My callbacks"], [true, "Team (due & overdue)"]].map(([v, t]) => (
      <button key={t} data-testid={`callback-tab-${v ? "team" : "mine"}`} onClick={() => onPick(v)} className={`h-9 px-4 text-sm border ${team === v ? "bg-primary text-primary-foreground border-primary" : "text-muted-foreground"}`}>{t}</button>))}
  </div>
);

const Queue = ({ d, team, agent, setAgent, agents, bump }) => {
  const shown = team && agent !== "All" ? d.items.filter((i) => i.agent === agent) : d.items;
  const reassigned = () => { bump(); window.dispatchEvent(new Event("callbacks-changed")); };
  return (
    <div className="ax-panel p-4" data-testid="callback-queue">
      <div className="flex flex-wrap items-center gap-2 mb-3"><PhoneCall className="h-4 w-4 text-primary" /><span className="ax-label">Due now</span>
        <span className="num" data-testid="callback-due-count">{d.due}</span>
        <span className="text-xs text-red-400" data-testid="callback-overdue-count">{d.overdue} overdue</span>
        {!team && <span className="text-xs text-muted-foreground">of {d.items.length} scheduled</span>}</div>
      {team && d.items.length > 0 && <AgentSummary items={d.items} agent={agent} setAgent={setAgent} />}
      {!shown.length ? <Empty text={team ? "No callbacks are due across the team." : "No callbacks scheduled."} /> : <div className="divide-y divide-border/50">{shown.map((l) => <div key={l.lead_id}><Row l={l} team={team} onDone={bump} />{team && <ReassignControl item={l} agents={agents} onDone={reassigned} />}</div>)}</div>}
    </div>
  );
};

export default function Callbacks() {
  const [team, setTeam] = useState(false);
  const [agent, setAgent] = useState("All");
  const [tick, setTick] = useState(0);
  const { d, setD, agents } = useCallbackData(team, tick);
  if (!d) return null;
  const bump = () => setTick((t) => t + 1);
  const pick = (v) => { setD(null); setAgent("All"); setTeam(v); };
  return (
    <div>
      <PageHeader eyebrow={team ? "Team callbacks" : "Your callbacks"} title="Call back now" desc={team ? "Due and overdue callbacks across all agents and both branches, earliest first." : "Leads you marked 'Didn't pick up', earliest first. A callback leaves this list once you log the call, a meeting or a WhatsApp on the lead."} />
      {d.can_team && <TeamTabs team={team} onPick={pick} />}
      {team && <CallbackScore refresh={tick} />}
      {team && <CallbackTrend refresh={tick} />}
      <Queue d={d} team={team} agent={agent} setAgent={setAgent} agents={agents} bump={bump} />
    </div>
  );
}
